import {
  AlertTriangle,
  Database,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { ApiError, api } from "../../api/client.js";
import type {
  ComponentRiskProfileState,
  RegisterRepositoryInput,
  RepositoryBusinessTierState,
  RepositoryComponentRiskItem,
  RepositoryRecord,
  ScanStatus,
} from "../../api/types.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { ScanStatusBadge } from "../../components/Badge.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import { formatDateTime, formatInteger, shortHash } from "../../lib/format.js";
import { ScanFamilyPicker } from "../scans/ScanFamilyPicker.js";
import { DEFAULT_SCAN_FAMILIES, type StaticScanFamily } from "../scans/scanFamilyScope.js";

function intakeErrorMessage(reason: unknown): string {
  const message = reason instanceof Error ? reason.message : "";
  if (
    reason instanceof ApiError &&
    (reason.code === "public_benchmark_target_not_allowlisted" ||
      message.includes("public_benchmark_target_not_allowlisted"))
  ) {
    return "This deployment accepts only sources approved for this evaluation. Choose a pre-staged local source or ask your Breakwater contact to authorize another source.";
  }
  return message || "The source could not be authorized.";
}

const ACTIVE_SCAN_STATUSES = new Set<ScanStatus>([
  "queued",
  "snapshotting",
  "detecting",
  "validating",
  "indexing",
]);

export function BusinessTierDialog({
  repository,
  onClose,
}: {
  repository: RepositoryRecord;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [tier, setTier] = useState<RepositoryBusinessTierState["tier"]>("tier_2");
  const [exploitability, setExploitability] = useState("");
  const [exposure, setExposure] = useState("");
  const [confidenceBand, setConfidenceBand] =
    useState<RepositoryBusinessTierState["confidenceBand"]>(null);
  const [moscaDataLifetimeMin, setMoscaDataLifetimeMin] = useState("");
  const [moscaDataLifetimeMax, setMoscaDataLifetimeMax] = useState("");
  const [moscaMigrationTimeMin, setMoscaMigrationTimeMin] = useState("");
  const [moscaMigrationTimeMax, setMoscaMigrationTimeMax] = useState("");
  const [moscaThreatHorizonMin, setMoscaThreatHorizonMin] = useState("");
  const [moscaThreatHorizonMax, setMoscaThreatHorizonMax] = useState("");
  const [moscaConfidenceBand, setMoscaConfidenceBand] =
    useState<RepositoryBusinessTierState["moscaConfidenceBand"]>(null);
  const [sourceType, setSourceType] = useState<RepositoryBusinessTierState["sourceType"]>("manual");
  const [sourceRef, setSourceRef] = useState("");
  const [rationale, setRationale] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    const controller = new AbortController();
    void api
      .getRepositoryBusinessTier(repository.id, controller.signal)
      .then(({ state }) => {
        if (!state) return;
        setTier(state.tier);
        setExploitability(state.exploitability?.toString() ?? "");
        setExposure(state.exposure?.toString() ?? "");
        setConfidenceBand(state.confidenceBand);
        setMoscaDataLifetimeMin(state.moscaDataLifetimeMin?.toString() ?? "");
        setMoscaDataLifetimeMax(state.moscaDataLifetimeMax?.toString() ?? "");
        setMoscaMigrationTimeMin(state.moscaMigrationTimeMin?.toString() ?? "");
        setMoscaMigrationTimeMax(state.moscaMigrationTimeMax?.toString() ?? "");
        setMoscaThreatHorizonMin(state.moscaThreatHorizonMin?.toString() ?? "");
        setMoscaThreatHorizonMax(state.moscaThreatHorizonMax?.toString() ?? "");
        setMoscaConfidenceBand(state.moscaConfidenceBand);
        setSourceType(state.sourceType);
        setSourceRef(state.sourceRef);
        setRationale(state.rationale);
        setOwnerId(state.ownerId);
        setExpiresAt(state.expiresAt?.slice(0, 16) ?? "");
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Business tier could not be loaded.");
      })
      .finally(() => setPending(false));
    return () => {
      controller.abort();
      if (dialog?.open) dialog.close();
    };
  }, [repository.id]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const riskInputs = [exploitability, exposure, confidenceBand];
      if (riskInputs.some(Boolean) && !riskInputs.every(Boolean)) {
        throw new Error(
          "Exploitability, exposure, and confidence must all be supplied or all left unavailable.",
        );
      }
      const moscaInputs = [
        moscaDataLifetimeMin,
        moscaDataLifetimeMax,
        moscaMigrationTimeMin,
        moscaMigrationTimeMax,
        moscaThreatHorizonMin,
        moscaThreatHorizonMax,
        moscaConfidenceBand,
      ];
      if (moscaInputs.some(Boolean) && !moscaInputs.every(Boolean)) {
        throw new Error(
          "All Mosca ranges and confidence must be supplied or all left unavailable.",
        );
      }
      await api.updateRepositoryBusinessTier(repository.id, {
        tier,
        exploitability: exploitability === "" ? null : Number(exploitability),
        exposure: exposure === "" ? null : Number(exposure),
        confidenceBand,
        moscaDataLifetimeMin: moscaDataLifetimeMin === "" ? null : Number(moscaDataLifetimeMin),
        moscaDataLifetimeMax: moscaDataLifetimeMax === "" ? null : Number(moscaDataLifetimeMax),
        moscaMigrationTimeMin: moscaMigrationTimeMin === "" ? null : Number(moscaMigrationTimeMin),
        moscaMigrationTimeMax: moscaMigrationTimeMax === "" ? null : Number(moscaMigrationTimeMax),
        moscaThreatHorizonMin: moscaThreatHorizonMin === "" ? null : Number(moscaThreatHorizonMin),
        moscaThreatHorizonMax: moscaThreatHorizonMax === "" ? null : Number(moscaThreatHorizonMax),
        moscaConfidenceBand,
        sourceType,
        sourceRef: sourceRef.trim(),
        rationale: rationale.trim(),
        ownerId: ownerId.trim(),
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Business tier could not be saved.");
    } finally {
      setPending(false);
    }
  };

  return (
    <dialog
      className="source-dialog"
      ref={dialogRef}
      aria-labelledby="business-tier-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="source-dialog__header">
        <div>
          <span className="eyebrow">Governed portfolio metadata</span>
          <h2 id="business-tier-dialog-title">Business tier · {repository.name}</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Close business tier editor"
          onClick={onClose}
        >
          <X aria-hidden="true" size={17} />
        </button>
      </div>
      <form className="source-dialog__form" onSubmit={(event) => void submit(event)}>
        <label className="form-field">
          <span>Business tier</span>
          <select
            value={tier}
            onChange={(event) => setTier(event.target.value as RepositoryBusinessTierState["tier"])}
          >
            <option value="tier_0">Tier 0 · highest criticality</option>
            <option value="tier_1">Tier 1</option>
            <option value="tier_2">Tier 2</option>
            <option value="tier_3">Tier 3</option>
          </select>
        </label>
        <div className="form-grid form-grid--two">
          <label className="form-field">
            <span>Exploitability (0–1, optional)</span>
            <input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={exploitability}
              onChange={(event) => setExploitability(event.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Exposure (0–1, optional)</span>
            <input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={exposure}
              onChange={(event) => setExposure(event.target.value)}
            />
          </label>
        </div>
        <label className="form-field">
          <span>Priority-input confidence</span>
          <select
            value={confidenceBand ?? ""}
            onChange={(event) =>
              setConfidenceBand(
                (event.target.value || null) as RepositoryBusinessTierState["confidenceBand"],
              )
            }
          >
            <option value="">Unavailable</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <small>These governed inputs never derive from technical severity.</small>
        </label>
        <fieldset className="form-field">
          <legend>Mosca migration urgency (optional)</legend>
          <p className="muted-copy">
            Enter uncertainty ranges in years for x + y &gt; z. This prioritizes migration runway;
            it does not predict or prove a breach.
          </p>
          {[
            [
              "x · Data-protection lifetime",
              moscaDataLifetimeMin,
              setMoscaDataLifetimeMin,
              moscaDataLifetimeMax,
              setMoscaDataLifetimeMax,
            ],
            [
              "y · Migration lead time",
              moscaMigrationTimeMin,
              setMoscaMigrationTimeMin,
              moscaMigrationTimeMax,
              setMoscaMigrationTimeMax,
            ],
            [
              "z · Quantum-threat horizon",
              moscaThreatHorizonMin,
              setMoscaThreatHorizonMin,
              moscaThreatHorizonMax,
              setMoscaThreatHorizonMax,
            ],
          ].map(([label, min, setMin, max, setMax]) => (
            <div className="form-grid form-grid--two" key={label as string}>
              <label className="form-field">
                <span>{label as string} · min</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={min as string}
                  onChange={(event) => (setMin as (value: string) => void)(event.target.value)}
                />
              </label>
              <label className="form-field">
                <span>{label as string} · max</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={max as string}
                  onChange={(event) => (setMax as (value: string) => void)(event.target.value)}
                />
              </label>
            </div>
          ))}
          <label className="form-field">
            <span>Mosca-input confidence</span>
            <select
              value={moscaConfidenceBand ?? ""}
              onChange={(event) =>
                setMoscaConfidenceBand(
                  (event.target.value ||
                    null) as RepositoryBusinessTierState["moscaConfidenceBand"],
                )
              }
            >
              <option value="">Unavailable</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
        </fieldset>
        <label className="form-field">
          <span>Evidence source type</span>
          <select
            value={sourceType}
            onChange={(event) =>
              setSourceType(event.target.value as RepositoryBusinessTierState["sourceType"])
            }
          >
            <option value="manual">Manual approval</option>
            <option value="authoritative_import">Authoritative import</option>
          </select>
        </label>
        <label className="form-field">
          <span>Evidence reference</span>
          <input
            required
            maxLength={512}
            value={sourceRef}
            onChange={(event) => setSourceRef(event.target.value)}
          />
        </label>
        <label className="form-field">
          <span>Rationale</span>
          <textarea
            required
            maxLength={2_000}
            rows={4}
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
          />
        </label>
        <label className="form-field">
          <span>Accountable owner</span>
          <input
            required
            maxLength={320}
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
          />
        </label>
        <label className="form-field">
          <span>Expiry (optional)</span>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
          <small>
            Expired evidence becomes unavailable; severity never substitutes for this tier.
          </small>
        </label>
        {error ? <InlineError error={new Error(error)} /> : null}
        <button className="button button--primary" type="submit" disabled={pending}>
          {pending ? "Loading…" : "Save governed risk profile"}
        </button>
      </form>
    </dialog>
  );
}

function ComponentRiskDialog({
  repository,
  onClose,
}: {
  repository: RepositoryRecord;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [items, setItems] = useState<RepositoryComponentRiskItem[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");
  const [tier, setTier] = useState<ComponentRiskProfileState["tier"]>("tier_2");
  const [exploitability, setExploitability] = useState("0.5");
  const [exposure, setExposure] = useState("0.5");
  const [confidenceBand, setConfidenceBand] =
    useState<ComponentRiskProfileState["confidenceBand"]>("medium");
  const [sourceType, setSourceType] = useState<ComponentRiskProfileState["sourceType"]>("manual");
  const [sourceRef, setSourceRef] = useState("");
  const [rationale, setRationale] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyItem = useCallback((item: RepositoryComponentRiskItem) => {
    const profile = item.profile;
    setSelectedKey(item.componentKey);
    setDisplayLabel(profile?.displayLabel ?? item.label);
    setTier(profile?.tier ?? "tier_2");
    setExploitability(String(profile?.exploitability ?? 0.5));
    setExposure(String(profile?.exposure ?? 0.5));
    setConfidenceBand(profile?.confidenceBand ?? "medium");
    setSourceType(profile?.sourceType ?? "manual");
    setSourceRef(profile?.sourceRef ?? "");
    setRationale(profile?.rationale ?? "");
    setOwnerId(profile?.ownerId ?? "");
    setExpiresAt(profile?.expiresAt?.slice(0, 16) ?? "");
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    const controller = new AbortController();
    void api
      .getRepositoryComponents(repository.id, controller.signal)
      .then(({ items: nextItems }) => {
        setItems(nextItems);
        const first = nextItems[0];
        if (first) applyItem(first);
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Components could not be loaded.");
      })
      .finally(() => setPending(false));
    return () => {
      controller.abort();
      if (dialog?.open) dialog.close();
    };
  }, [repository.id, applyItem]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedKey) return;
    setPending(true);
    setError(null);
    try {
      const profile = await api.updateComponentRiskProfile(repository.id, selectedKey, {
        displayLabel: displayLabel.trim(),
        tier,
        exploitability: Number(exploitability),
        exposure: Number(exposure),
        confidenceBand,
        sourceType,
        sourceRef: sourceRef.trim(),
        rationale: rationale.trim(),
        ownerId: ownerId.trim(),
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setItems((current) =>
        current.map((item) => (item.componentKey === selectedKey ? { ...item, profile } : item)),
      );
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Component profile could not be saved.");
    } finally {
      setPending(false);
    }
  };

  return (
    <dialog
      className="source-dialog"
      ref={dialogRef}
      aria-labelledby="component-risk-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="source-dialog__header">
        <div>
          <span className="eyebrow">Observed component governance</span>
          <h2 id="component-risk-dialog-title">Component priority · {repository.name}</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Close component priority editor"
          onClick={onClose}
        >
          <X aria-hidden="true" size={17} />
        </button>
      </div>
      {items.length === 0 && !pending ? (
        <EmptyState
          title="No observed component identities"
          description="Run a scan that records a canonical package or image identity before governing component risk."
        />
      ) : (
        <form className="source-dialog__form" onSubmit={(event) => void submit(event)}>
          <label className="form-field">
            <span>Observed component</span>
            <select
              value={selectedKey}
              required
              onChange={(event) => {
                const item = items.find(
                  (candidate) => candidate.componentKey === event.target.value,
                );
                if (item) applyItem(item);
              }}
            >
              {items.map((item) => (
                <option value={item.componentKey} key={item.componentKey}>
                  {item.label}
                  {item.profile ? " · governed" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span>Governed display label</span>
            <input
              required
              maxLength={320}
              value={displayLabel}
              onChange={(event) => setDisplayLabel(event.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Business tier</span>
            <select
              value={tier}
              onChange={(event) => setTier(event.target.value as ComponentRiskProfileState["tier"])}
            >
              <option value="tier_0">Tier 0 · highest criticality</option>
              <option value="tier_1">Tier 1</option>
              <option value="tier_2">Tier 2</option>
              <option value="tier_3">Tier 3</option>
            </select>
          </label>
          <div className="form-grid form-grid--two">
            <label className="form-field">
              <span>Exploitability (0–1)</span>
              <input
                required
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={exploitability}
                onChange={(event) => setExploitability(event.target.value)}
              />
            </label>
            <label className="form-field">
              <span>Exposure (0–1)</span>
              <input
                required
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={exposure}
                onChange={(event) => setExposure(event.target.value)}
              />
            </label>
          </div>
          <label className="form-field">
            <span>Confidence</span>
            <select
              value={confidenceBand}
              onChange={(event) =>
                setConfidenceBand(event.target.value as ComponentRiskProfileState["confidenceBand"])
              }
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label className="form-field">
            <span>Evidence source type</span>
            <select
              value={sourceType}
              onChange={(event) =>
                setSourceType(event.target.value as ComponentRiskProfileState["sourceType"])
              }
            >
              <option value="manual">Manual approval</option>
              <option value="authoritative_import">Authoritative import</option>
            </select>
          </label>
          <label className="form-field">
            <span>Evidence reference</span>
            <input
              required
              maxLength={512}
              value={sourceRef}
              onChange={(event) => setSourceRef(event.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Rationale</span>
            <textarea
              required
              maxLength={2_000}
              rows={4}
              value={rationale}
              onChange={(event) => setRationale(event.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Accountable owner</span>
            <input
              required
              maxLength={320}
              value={ownerId}
              onChange={(event) => setOwnerId(event.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Expiry (optional)</span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
            <small>
              Profiles older than 30 days or past expiry cannot produce a priority score.
            </small>
          </label>
          {error ? <InlineError error={new Error(error)} /> : null}
          <button
            className="button button--primary"
            type="submit"
            disabled={pending || !selectedKey}
          >
            {pending ? "Loading…" : "Save component profile"}
          </button>
        </form>
      )}
    </dialog>
  );
}

function RegisterSourceDialog({
  onClose,
  onRegister,
}: {
  onClose: () => void;
  onRegister: (
    input: RegisterRepositoryInput,
    startAfterRegistration: boolean,
    analysisFamilies: readonly StaticScanFamily[],
  ) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameId = useId();
  const rootId = useId();
  const urlId = useId();
  const authorizationId = useId();
  const [name, setName] = useState("");
  const [authorizedRoot, setAuthorizedRoot] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [expectedCommit, setExpectedCommit] = useState("");
  const [sourceMode, setSourceMode] = useState<"github" | "local">("local");
  const [authorizationRef, setAuthorizationRef] = useState("");
  const [startAfterRegistration, setStartAfterRegistration] = useState(true);
  const [analysisFamilies, setAnalysisFamilies] =
    useState<readonly StaticScanFamily[]>(DEFAULT_SCAN_FAMILIES);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onRegister(
        {
          name: name.trim(),
          ...(sourceMode === "github"
            ? {
                sourceUrl: sourceUrl.trim(),
                ...(expectedCommit.trim() ? { expectedCommit: expectedCommit.trim() } : {}),
              }
            : { authorizedRoot: authorizedRoot.trim() }),
          authorizationRef: authorizationRef.trim(),
        },
        startAfterRegistration,
        analysisFamilies,
      );
      onClose();
    } catch (reason) {
      setError(intakeErrorMessage(reason));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <dialog
      className="source-dialog"
      ref={dialogRef}
      aria-labelledby="source-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="source-dialog__header">
        <div>
          <span className="eyebrow">Authorized intake</span>
          <h2 id="source-dialog-title">Add repository</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Close add repository dialog"
          title="Close add repository dialog"
          onClick={onClose}
        >
          <X aria-hidden="true" size={17} />
        </button>
      </div>
      <form className="source-dialog__form" onSubmit={submit}>
        <fieldset className="source-mode-picker">
          <legend>Source location</legend>
          <label>
            <input
              type="radio"
              name="source-mode"
              checked={sourceMode === "github"}
              onChange={() => setSourceMode("github")}
            />
            Public GitHub repository
          </label>
          <label>
            <input
              type="radio"
              name="source-mode"
              checked={sourceMode === "local"}
              onChange={() => setSourceMode("local")}
            />
            Pre-staged local source
          </label>
        </fieldset>
        {sourceMode === "github" ? (
          <label className="form-field" htmlFor={urlId}>
            <span>GitHub repository URL</span>
            <input
              id={urlId}
              type="url"
              required
              maxLength={2048}
              value={sourceUrl}
              placeholder="https://github.com/owner/repository"
              spellCheck={false}
              onChange={(event) => {
                const value = event.target.value;
                setSourceUrl(value);
                if (!name) {
                  const inferred = value.match(/github\.com\/[^/]+\/([^/?#]+?)(?:\.git)?$/u)?.[1];
                  if (inferred) setName(inferred);
                }
              }}
            />
            <small>
              Customer-demo intake: a public github.com repository is fetched read-only and pinned
              to an immutable commit at authorize time. Private repos, credentials, hooks,
              submodules, and source execution remain disabled. Only sources approved for this
              evaluation can be authorized; public visibility alone is not authorization. Ask your
              Breakwater contact to authorize another source if needed.
            </small>
          </label>
        ) : null}
        {sourceMode === "github" ? (
          <label className="form-field" htmlFor={`${urlId}-commit`}>
            <span>Commit SHA (optional)</span>
            <input
              id={`${urlId}-commit`}
              value={expectedCommit}
              maxLength={40}
              spellCheck={false}
              placeholder="Leave blank to pin the current default-branch commit"
              onChange={(event) => setExpectedCommit(event.target.value.trim())}
            />
          </label>
        ) : null}
        <label className="form-field" htmlFor={nameId}>
          <span>Repository name</span>
          <input
            id={nameId}
            required
            maxLength={120}
            value={name}
            placeholder="payments-service"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        {sourceMode === "local" ? (
          <label className="form-field" htmlFor={rootId}>
            <span>Authorized local root</span>
            <input
              id={rootId}
              required
              maxLength={4096}
              value={authorizedRoot}
              placeholder="/absolute/path/inside/an/allowed/source/root"
              spellCheck={false}
              onChange={(event) => setAuthorizedRoot(event.target.value)}
            />
          </label>
        ) : null}
        <label className="form-field" htmlFor={authorizationId}>
          <span>Authorization reference</span>
          <input
            id={authorizationId}
            required
            minLength={3}
            maxLength={500}
            value={authorizationRef}
            placeholder="Owner-approved security assessment"
            onChange={(event) => setAuthorizationRef(event.target.value)}
          />
        </label>
        <ScanFamilyPicker selected={analysisFamilies} onChange={setAnalysisFamilies} />
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={startAfterRegistration}
            onChange={(event) => setStartAfterRegistration(event.target.checked)}
          />
          <span>Start the first static scan immediately</span>
        </label>
        {error ? (
          <div className="inline-alert inline-alert--error" role="alert">
            <AlertTriangle aria-hidden="true" size={16} />
            <span>{error}</span>
          </div>
        ) : null}
        <div className="source-dialog__actions">
          <button className="button" type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="button button--primary" type="submit" disabled={submitting}>
            <ShieldCheck aria-hidden="true" size={15} />
            {submitting ? "Authorizing" : "Authorize source"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

export function SourcesPage({
  onOpenScans,
  canManage = true,
  canManageBusinessTier = false,
}: {
  onOpenScans: () => void;
  canManage?: boolean | undefined;
  canManageBusinessTier?: boolean | undefined;
}) {
  const [query, setQuery] = useState("");
  const [showRegistration, setShowRegistration] = useState(false);
  const [tierRepository, setTierRepository] = useState<RepositoryRecord | null>(null);
  const [componentRepository, setComponentRepository] = useState<RepositoryRecord | null>(null);
  const [startingRepositoryId, setStartingRepositoryId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const debouncedQuery = useDebouncedValue(query, 250);
  const loadRepositories = useCallback(
    (signal: AbortSignal) => api.getRepositories(debouncedQuery, signal),
    [debouncedQuery],
  );
  const repositories = useApiResource(loadRepositories);
  const items = repositories.data?.items ?? [];
  const partialSources = items.filter(
    (repository) =>
      repository.latestScan?.status === "completed_with_errors" ||
      (repository.latestScan?.filesFailed ?? 0) > 0 ||
      (repository.latestScan?.filesSkipped ?? 0) > 0,
  ).length;

  const startScan = async (repository: RepositoryRecord) => {
    setStartingRepositoryId(repository.id);
    setActionError(null);
    try {
      await api.startScan(repository.id);
      repositories.refresh();
      onOpenScans();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "The scan could not be started.");
    } finally {
      setStartingRepositoryId(null);
    }
  };

  const registerSource = async (
    input: RegisterRepositoryInput,
    startAfterRegistration: boolean,
    analysisFamilies: readonly StaticScanFamily[],
  ) => {
    const repository = await api.registerRepository(input);
    if (startAfterRegistration) {
      await api.startScan(repository.id, `web-${crypto.randomUUID()}`, undefined, analysisFamilies);
    }
    repositories.refresh();
    if (startAfterRegistration) onOpenScans();
  };

  return (
    <section className="page" aria-labelledby="sources-heading">
      {tierRepository ? (
        <BusinessTierDialog repository={tierRepository} onClose={() => setTierRepository(null)} />
      ) : null}
      {componentRepository ? (
        <ComponentRiskDialog
          repository={componentRepository}
          onClose={() => setComponentRepository(null)}
        />
      ) : null}
      <div className="page-heading">
        <div>
          <h1 id="sources-heading">Sources</h1>
          <p>Authorized repositories, immutable scan revisions, and latest coverage state.</p>
        </div>
        <div className="page-heading__actions">
          {canManage ? (
            <button
              className="button button--primary"
              type="button"
              onClick={() => setShowRegistration(true)}
            >
              <Plus aria-hidden="true" size={15} />
              Add repository
            </button>
          ) : null}
          <button
            className="icon-button"
            type="button"
            aria-label="Refresh sources"
            title="Refresh sources"
            disabled={repositories.isRefreshing}
            onClick={repositories.refresh}
          >
            <RefreshCw
              aria-hidden="true"
              className={repositories.isRefreshing ? "spin" : undefined}
              size={17}
            />
          </button>
        </div>
      </div>

      {showRegistration && canManage ? (
        <RegisterSourceDialog
          onClose={() => setShowRegistration(false)}
          onRegister={registerSource}
        />
      ) : null}

      <section className="summary-line" aria-label="Source summary">
        <span>
          <Database aria-hidden="true" size={15} />
          <strong>{formatInteger(repositories.data?.total ?? 0)}</strong> authorized sources
        </span>
        <span className={partialSources > 0 ? "text-warning" : undefined}>
          <AlertTriangle aria-hidden="true" size={15} />
          <strong>{formatInteger(partialSources)}</strong> latest runs with coverage gaps
        </span>
        <span>
          <ShieldCheck aria-hidden="true" size={15} /> Static-only acquisition policy
        </span>
      </section>

      <div className="list-toolbar">
        <label className="search-control">
          <Search aria-hidden="true" size={16} />
          <span className="sr-only">Search sources</span>
          <input
            type="search"
            value={query}
            placeholder="Search repository or authorization"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {query ? (
          <button className="text-button" type="button" onClick={() => setQuery("")}>
            Clear all
          </button>
        ) : null}
      </div>

      {repositories.error && repositories.data ? (
        <InlineError error={repositories.error} onRetry={repositories.refresh} />
      ) : null}
      {actionError ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{actionError}</span>
        </div>
      ) : null}
      {repositories.isLoading && !repositories.data ? <LoadingTable columns={7} rows={8} /> : null}
      {repositories.error && !repositories.data ? (
        <InlineError error={repositories.error} onRetry={repositories.refresh} />
      ) : null}
      {!repositories.isLoading && repositories.data && items.length === 0 ? (
        <EmptyState
          title="No authorized sources found"
          description={
            query
              ? "No repository matches the current search."
              : "Repositories will appear after an operator authorizes a local source root."
          }
        />
      ) : null}

      {items.length > 0 ? (
        <section className="table-scroll" aria-label="Authorized sources table">
          <table className="data-table sources-table">
            <thead>
              <tr>
                <th>Repository</th>
                <th>Authorization</th>
                <th>Authorized root</th>
                <th>Latest scan</th>
                <th>Coverage accounting</th>
                <th>Snapshot</th>
                <th>Registered</th>
                {canManage || canManageBusinessTier ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((repository) => {
                const scan = repository.latestScan;
                const incomplete =
                  scan?.status === "completed_with_errors" ||
                  (scan?.filesFailed ?? 0) > 0 ||
                  (scan?.filesSkipped ?? 0) > 0;
                const active = scan ? ACTIVE_SCAN_STATUSES.has(scan.status) : false;
                return (
                  <tr className="data-row" key={repository.id}>
                    <td>
                      <strong>{repository.name}</strong>
                      {repository.sourceUrl ? (
                        <small className="cell-secondary">
                          Intentionally vulnerable public benchmark
                        </small>
                      ) : null}
                      <small className="cell-secondary">{repository.id}</small>
                    </td>
                    <td>{repository.authorizationRef}</td>
                    <td className="source-root" title={repository.authorizedRoot}>
                      <code>{repository.authorizedRoot}</code>
                    </td>
                    <td>
                      {scan ? (
                        <>
                          <ScanStatusBadge status={scan.status} />
                          <small className="cell-secondary">{formatDateTime(scan.startedAt)}</small>
                        </>
                      ) : (
                        <span className="muted-copy">Not scanned</span>
                      )}
                    </td>
                    <td>
                      {scan ? (
                        <span
                          className={
                            incomplete ? "coverage-summary text-warning" : "coverage-summary"
                          }
                        >
                          {incomplete ? <AlertTriangle aria-hidden="true" size={14} /> : null}
                          {scan.filesScanned} scanned · {scan.filesSkipped} skipped ·{" "}
                          {scan.filesFailed} failed
                        </span>
                      ) : (
                        <span className="muted-copy">No coverage record</span>
                      )}
                    </td>
                    <td>
                      {scan?.snapshotId ? (
                        <code>{shortHash(scan.snapshotId, 9)}</code>
                      ) : scan ? (
                        <span className="muted-copy">Pending</span>
                      ) : (
                        <span className="muted-copy">—</span>
                      )}
                    </td>
                    <td>{formatDateTime(repository.createdAt)}</td>
                    {canManage || canManageBusinessTier ? (
                      <td className="column-action">
                        <div className="table-action-stack">
                          {canManage ? (
                            <button
                              className="button button--small"
                              type="button"
                              disabled={active || startingRepositoryId === repository.id}
                              onClick={() => void startScan(repository)}
                            >
                              {scan ? (
                                <RotateCcw aria-hidden="true" size={13} />
                              ) : (
                                <Play aria-hidden="true" size={13} />
                              )}
                              {startingRepositoryId === repository.id
                                ? "Starting"
                                : active
                                  ? "Running"
                                  : scan
                                    ? "Rescan"
                                    : "Scan"}
                            </button>
                          ) : null}
                          {canManageBusinessTier ? (
                            <>
                              <button
                                className="button button--small"
                                type="button"
                                onClick={() => setTierRepository(repository)}
                              >
                                Business tier
                              </button>
                              <button
                                className="button button--small"
                                type="button"
                                onClick={() => setComponentRepository(repository)}
                              >
                                Components
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : null}
    </section>
  );
}
